// variants.js

if (!customElements.get("variant-options")) {
  customElements.define(
    "variant-options",
    class VariantOptions extends HTMLElement {
      whenLoaded = Promise.all([customElements.whenDefined("gallery-section")]);

      constructor() {
        super();
        this.onVariantChange = this.onVariantChange.bind(this);
        this.onKeyDown = this.onKeyDown.bind(this);

        this.addEventListener("change", this.onVariantChange);
        this.addEventListener("keydown", this.onKeyDown);
      }

      connectedCallback() {
        this.whenLoaded.then(() => this.initialize());
      }

      initialize() {
        this.stockFilter = "in-stock";
        this.updateOptions();
        this.updateMasterId();
        this.updateGallery();
        this.setupStockFilter();
        this.updateVariantStatuses();
        this.updateDropdownButtons();

        // Bind a fabric->color listener after initial render
        this.bindFabricChangeListener();
      }

      onKeyDown(event) {
        if (event.key === "Enter" || event.keyCode === 13) {
          const input = event.target.querySelector("input");
          if (input) input.click();
        }
      }

      onVariantChange() {
        this.dispatchEvent(
          new CustomEvent("variantChangeStart", { bubbles: true, composed: true })
        );

        this.toggleAddButton(true, "", false);

        this.updateOptions();
        this.updateMasterId();
        this.updateGallery();
        this.updatePickupAvailability();
        this.removeErrorMessage();

        this.updateVariantStatuses();
        this.updateDropdownButtons();

        if (!this.currentVariant) {
          this.setUnavailable();
        } else {
          this.updateMedia();
          this.lenOfVariantOptions = document.querySelectorAll("variant-options").length;
          if (this.lenOfVariantOptions === 1) this.updateURL();
          this.updateVariantInput();
          this.renderProductInfo();
          this.updateShareUrl();

          // If inventory is not tracked, Shopify returns available=true.
          const available = this.currentVariant?.available ?? true;
          this.toggleAddButton(!available, available ? "" : window.variantStrings.soldOut, true);
        }

        this.dispatchEvent(
          new CustomEvent("variantChangeEnd", { bubbles: true, composed: true })
        );
      }

      /**
       * Listen for changes in the fabric fieldset, then pick the first
       * available color input that respects the active stock filter.
       */
      bindFabricChangeListener() {
        const fieldsets = Array.from(this.querySelectorAll(".wt-product__option"));
        if (fieldsets.length < 2) return;

        const fabricIndex = fieldsets.length === 2 ? 0 : 1;
        const fabricFs = fieldsets[fabricIndex];
        if (!fabricFs) return;

        var self = this;
        fabricFs.querySelectorAll('input[type="radio"]').forEach(function(radio) {
          radio.addEventListener("change", function() {
            // Wait for updateVariantStatuses + applyStockFilter to run first
            setTimeout(function() {
              // When fabric changes, reset to "In Stock" if it's available for the new fabric
              var filter = self.querySelector(".bb-stock-filter");
              if (filter && self.stockFilter !== "in-stock") {
                var inStockBtn = filter.querySelector('[data-filter="in-stock"]');
                if (inStockBtn && inStockBtn.style.display !== "none") {
                  filter.querySelectorAll(".bb-stock-filter__btn").forEach(function(b) {
                    b.classList.remove("bb-stock-filter__btn--active");
                  });
                  inStockBtn.classList.add("bb-stock-filter__btn--active");
                  self.stockFilter = "in-stock";
                  var fieldsets = Array.from(self.querySelectorAll(".wt-product__option"));
                  self.applyStockFilter(fieldsets);
                }
              }
              self.autoSelectFirstAvailableColour();
            }, 0);
          });
        });
      }

      // ----- In Stock / Made to Order toggle -----

      setupStockFilter() {
        var filter = this.querySelector(".bb-stock-filter");
        if (!filter) return;

        // Use Liquid-embedded data-bb-stock-type attributes (server-side truth)
        var hasInStock = !!this.querySelector('[data-bb-stock-type="in-stock"]');
        var hasMTO = !!this.querySelector('[data-bb-stock-type="made-to-order"]');

        // No colour data at all — product doesn't use this feature
        if (!hasInStock && !hasMTO) {
          filter.style.display = "none";
          this.stockFilter = "all";
          return;
        }

        // Show only the button(s) that match globally available types
        var inStockBtn = filter.querySelector('[data-filter="in-stock"]');
        var mtoBtn = filter.querySelector('[data-filter="made-to-order"]');
        if (inStockBtn) inStockBtn.style.display = hasInStock ? "" : "none";
        if (mtoBtn) mtoBtn.style.display = hasMTO ? "" : "none";

        // Set the initial active button to the first available type
        var defaultFilter = hasInStock ? "in-stock" : "made-to-order";
        this.stockFilter = defaultFilter;
        filter.querySelectorAll(".bb-stock-filter__btn").forEach(function(b) {
          b.classList.toggle("bb-stock-filter__btn--active", b.dataset.filter === defaultFilter);
        });

        // Attach click listeners (only clickable buttons matter)
        var self = this;
        filter.querySelectorAll(".bb-stock-filter__btn").forEach(function(btn) {
          btn.addEventListener("click", function() {
            if (btn.classList.contains("bb-stock-filter__btn--active")) return;
            filter.querySelectorAll(".bb-stock-filter__btn").forEach(function(b) {
              b.classList.remove("bb-stock-filter__btn--active");
            });
            btn.classList.add("bb-stock-filter__btn--active");
            self.stockFilter = btn.dataset.filter;
            self.updateVariantStatuses();
            self.autoSelectFirstAvailableColour();
          });
        });
      }

      updateStockFilterVisibility(fieldsets) {
        var filter = this.querySelector(".bb-stock-filter");
        if (!filter || !fieldsets) return;

        var colorFs = null;
        for (var i = 0; i < fieldsets.length; i++) {
          if (fieldsets[i].querySelector("[data-bb-stock-type]")) {
            colorFs = fieldsets[i];
            break;
          }
        }
        if (!colorFs) return;

        // Check which stock types exist for the currently selected fabric/size
        // (only look at items NOT hidden by the availability logic)
        var hasInStock = false;
        var hasMTO = false;
        Array.from(colorFs.querySelectorAll("li")).forEach(function(li) {
          if (li.classList.contains("is-hidden")) return;
          var t = li.dataset.bbStockType;
          if (t === "in-stock") hasInStock = true;
          if (t === "made-to-order") hasMTO = true;
        });

        var inStockBtn = filter.querySelector('[data-filter="in-stock"]');
        var mtoBtn = filter.querySelector('[data-filter="made-to-order"]');

        // Show or hide each button independently
        if (inStockBtn) inStockBtn.style.display = hasInStock ? "" : "none";
        if (mtoBtn) mtoBtn.style.display = hasMTO ? "" : "none";

        // If the currently active button just got hidden, switch to the visible one
        var activeBtn = filter.querySelector(".bb-stock-filter__btn--active");
        if (activeBtn && activeBtn.style.display === "none") {
          var fallback = hasInStock ? inStockBtn : mtoBtn;
          if (fallback) {
            filter.querySelectorAll(".bb-stock-filter__btn").forEach(function(b) {
              b.classList.remove("bb-stock-filter__btn--active");
            });
            fallback.classList.add("bb-stock-filter__btn--active");
            this.stockFilter = fallback.dataset.filter;
          }
        }

      }

      applyStockFilter(fieldsets) {
        if (!this.stockFilter || this.stockFilter === "all") return;

        var self = this;
        fieldsets.forEach(function(fs) {
          if (!fs.querySelector("[data-bb-stock-type]")) return;

          Array.from(fs.querySelectorAll('input[type="radio"]')).forEach(function(input) {
            var li = input.closest("li");
            if (!li) return;

            var stockType = li.dataset.bbStockType;
            if (!stockType) return; // no attribute = not a colour swatch, skip

            var matchesFilter = stockType === self.stockFilter;

            li.classList.toggle("bb-filter-hidden", !matchesFilter);
            if (!matchesFilter) {
              li.setAttribute("aria-hidden", "true");
              li.setAttribute("inert", "");
            } else if (!li.classList.contains("is-hidden")) {
              li.removeAttribute("aria-hidden");
              li.removeAttribute("inert");
            }
          });
        });
      }

      autoSelectFirstAvailableColour() {
        var fieldsets = Array.from(this.querySelectorAll(".wt-product__option"));
        var colorFs = fieldsets.find(function(fs) {
          return fs.querySelector("[data-bb-stock-type]");
        });
        if (!colorFs) return;

        var items = Array.from(colorFs.querySelectorAll("li")).filter(function(li) {
          var inp = li.querySelector("input");
          return inp &&
            !li.classList.contains("is-hidden") &&
            !li.classList.contains("bb-filter-hidden") &&
            !inp.disabled;
        });
        // Prefer a colour that is actually available (in stock); fall back to any visible one
        var firstAvail = items.find(function(li) {
          return li.querySelector("input").dataset.bbAvailable === "true";
        }) || items[0];

        var input = firstAvail && firstAvail.querySelector("input");
        if (input && !input.checked) {
          input.checked = true;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        }
      }

      // ----- Core variant logic (unchanged from Wonder theme) -----

      updateOptions() {
        const fieldsets = Array.from(this.querySelectorAll(".wt-product__option"));
        this.options = fieldsets.map((fs) => {
          const sel = Array.from(fs.querySelectorAll("input")).find((i) => i.checked);
          return sel ? sel.value : "";
        });
      }

      updateMasterId() {
        const variants = this.getVariantData();
        this.currentVariant = variants.find((v) =>
          v.options.every((opt, i) => this.options[i] === opt)
        );
      }

      updateGallery() {
        const gallery = document.getElementById("MediaGallery-" + this.dataset.section);
        const mediaId = this.currentVariant?.featured_media?.id || false;
        if (gallery) gallery.filterSlides(this.options, mediaId, true);
      }

      updateColourStockTypes() {
        var fieldsets = Array.from(this.querySelectorAll(".wt-product__option"));
        if (fieldsets.length < 2) return;
        var lastOptIdx = this.options.length - 1;
        var variants = Array.isArray(this.variantData) ? this.variantData : [];
        var self = this;
        var lastFs = fieldsets[fieldsets.length - 1];
        Array.from(lastFs.querySelectorAll("li[data-bb-stock-type]")).forEach(function(li) {
          var inp = li.querySelector("input");
          if (!inp) return;
          var colourValue = inp.value;
          var isTracked = variants.some(function(v) {
            if (v.options[lastOptIdx] !== colourValue) return false;
            if (v.inventory_management !== "shopify") return false;
            for (var i = 0; i < lastOptIdx; i++) {
              if (v.options[i] !== self.options[i]) return false;
            }
            return true;
          });
          li.setAttribute("data-bb-stock-type", isTracked ? "in-stock" : "made-to-order");
        });
      }

      updateVariantStatuses() {
        const variants = Array.isArray(this.variantData) ? this.variantData : [];
        const fieldsets = Array.from(this.querySelectorAll(".wt-product__option"));

        fieldsets.forEach((fs, idx) => {
          if (idx === 0) return;
          const inputs = Array.from(fs.querySelectorAll('input[type="radio"]'));
          const prev = this.options[idx - 1];
          // exists = any variant for this fabric (tracked or not, available or not)
          // available = actually purchasable right now
          const exists = variants
            .filter((v) => v.options[idx - 1] === prev)
            .map((v) => v.options[idx]);
          const available = variants
            .filter((v) => v.available && v.options[idx - 1] === prev)
            .map((v) => v.options[idx]);

          inputs.forEach((input) => {
            const doesExist = exists.includes(input.value);
            const isAvailable = available.includes(input.value);

            // Strike-through only when the colour has no variant at all for this fabric
            input.disabled = !doesExist;
            input.classList.toggle("disabled", !doesExist);
            // Store real availability so autoSelect can prefer in-stock colours
            input.dataset.bbAvailable = isAvailable ? "true" : "false";

            // Hide swatches that have no variant for this fabric at all
            const li = input.closest("li");
            if (li) {
              li.classList.toggle("is-hidden", !doesExist);
              if (!doesExist) {
                li.setAttribute("aria-hidden", "true");
                li.setAttribute("inert", "");
              } else {
                li.removeAttribute("aria-hidden");
                li.removeAttribute("inert");
              }
            }
          });
        });

        // Refresh per-colour stock types based on the currently selected fabric
        try {
          this.updateColourStockTypes();
        } catch (e) {
          console.warn("[BB] Stock type update error:", e);
        }

        // Update toggle visibility based on what's available for the current fabric selection
        try {
          this.updateStockFilterVisibility(fieldsets);
        } catch (e) {
          console.warn("[BB] Stock filter visibility error:", e);
        }

        // Apply the In Stock / Made to Order filter (protected so it never breaks variant flow)
        try {
          this.applyStockFilter(fieldsets);
        } catch (e) {
          console.warn("[BB] Stock filter error:", e);
        }
      }

      updateDropdownButtons() {
        const fieldsets = Array.from(this.querySelectorAll(".wt-product__option"));
        fieldsets.forEach((fs) => {
          const sel = fs.querySelector("input:checked");
          const title = fs.querySelector(".wt-product__option__title .value");
          const span = fs.querySelector(".wt-product__option__dropdown span");
          if (sel && title) title.textContent = sel.value;
          if (sel && span) span.textContent = sel.value;

          const dropdown = fs.querySelector(".wt-product__option__dropdown");
          if (dropdown) {
            dropdown.classList.toggle(
              "wt-product__option__dropdown--unavailable",
              sel?.disabled
            );
          }
        });
      }

      updateMedia() {
        if (!this.currentVariant?.featured_media) return;
        this.setAttribute("data-variant-id", this.currentVariant.id);
        this.setAttribute(
          "data-featured-image",
          this.currentVariant.featured_media.preview_image.src
        );
        this.setAttribute("data-featured-image-id", this.currentVariant.featured_media.id);

        const modal = document.querySelector(
          "#ProductModal-" + this.dataset.section + " .product-media-modal__content"
        );
        const slide = modal?.querySelector(
          "[data-media-id=\"" + this.currentVariant.featured_media.id + "\"]"
        );
        if (modal && slide) modal.prepend(slide);
      }

      updateURL() {
        if (!this.currentVariant || this.dataset.updateUrl === "false") return;
        window.history.replaceState(
          {},
          "",
          this.dataset.url + "?variant=" + this.currentVariant.id
        );
      }

      updateShareUrl() {
        const btn = document.getElementById("Share-" + this.dataset.section);
        if (btn && btn.updateUrl) {
          btn.updateUrl(window.shopUrl + this.dataset.url + "?variant=" + this.currentVariant.id);
        }
      }

      updateVariantInput() {
        const selector = "#product-form-" + this.dataset.section + ", #product-form-installment-" + this.dataset.section;
        document.querySelectorAll(selector).forEach((form) => {
          const input = form.querySelector('input[name="id"]');
          if (!input) return;
          input.value = this.currentVariant.id;
          input.dispatchEvent(new Event("change", { bubbles: true }));
        });
      }

      updatePickupAvailability() {
        const pu = document.querySelector("pickup-availability");
        if (!pu) return;
        pu.dataset.variantId = this.currentVariant?.id || "";
        if (this.currentVariant?.available) pu.fetchAvailability(this.currentVariant.id);
        else {
          pu.removeAttribute("available");
          pu.innerHTML = "";
        }
      }

      removeErrorMessage() {
        const section = this.closest("section");
        const frm = section?.querySelector("product-form");
        if (frm && typeof frm.handleErrorMessage === "function") {
          frm.handleErrorMessage();
        }
      }

      renderProductInfo() {
        const vid = this.currentVariant.id;
        const sectionId = this.dataset.originalSection || this.dataset.section;

        fetch(this.dataset.url + "?variant=" + vid + "&section_id=" + sectionId)
          .then((r) => r.text())
          .then((txt) => {
            if (this.currentVariant.id !== vid) return;

            const doc = new DOMParser().parseFromString(txt, "text/html");

            ["price", "Sku", "Inventory"].forEach((key) => {
              const src = doc.getElementById(key + "-" + sectionId);
              const dst = document.getElementById(key + "-" + this.dataset.section);
              if (src && dst) {
                dst.innerHTML = src.innerHTML;
                dst.classList.remove("visibility-hidden");
              }
            });

            const addButtonUpdated = doc.getElementById("ProductSubmitButton-" + sectionId);
            if (addButtonUpdated) {
              const disabled = addButtonUpdated.hasAttribute("disabled");
              this.toggleAddButton(disabled, disabled ? window.variantStrings.soldOut : "", true);
            } else {
              const available = this.currentVariant?.available ?? true;
              this.toggleAddButton(!available, available ? "" : window.variantStrings.soldOut, true);
            }
          });
      }

      toggleAddButton(disable = true, text = "", modifyClass = true) {
        const form = document.getElementById("product-form-" + this.dataset.section);
        const btn = form?.querySelector('[name="add"]');
        const sp = btn?.querySelector("span");
        if (!btn) return;

        if (disable) {
          btn.setAttribute("disabled", "");
          if (text && sp) sp.textContent = text;
        } else {
          btn.removeAttribute("disabled");
          if (sp) sp.textContent = window.variantStrings.addToCart;
        }
      }

      setUnavailable() {
        const form = document.getElementById("product-form-" + this.dataset.section);
        const span = form?.querySelector('[name="add"] > span');
        if (span) span.textContent = window.variantStrings.unavailable;
        const priceEl = document.getElementById("price-" + this.dataset.section);
        if (priceEl) priceEl.classList.add("visibility-hidden");
        const final = priceEl?.querySelector(".price-item--regular");
        if (final) final.innerText = this.dataset.lowestPrice;
        ["Inventory", "Sku"].forEach((k) => {
          const el = document.getElementById(k + "-" + this.dataset.section);
          if (el) el.classList.add("visibility-hidden");
        });
      }

      getVariantData() {
        if (!this.variantData) {
          const script = this.querySelector('script[type="application/json"]');
          this.variantData = script ? JSON.parse(script.textContent) : [];
        }
        return this.variantData;
      }
    }
  );
}
